import {
  centerAreaBounds,
  finderPatternOrigins,
  isCenterAreaModule,
  isFinderPatternModule,
  isFunctionPatternModule,
  FINDER_PATTERN_SIZE,
  type QrMatrix,
} from '../domain/matrix';
import type { QrDesign } from '../domain/types';
import { eyePaths } from './eyes';
import { modulePath } from './modules';

/**
 * The quiet zone required by ISO/IEC 18004. The previous renderer drew the
 * symbol edge-to-edge with no margin, which is the single most common cause of
 * a technically valid QR code that scanners refuse to read.
 */
export const QUIET_ZONE_MODULES = 4;

/** A square region, in output pixels. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

/** One fill operation: concatenated path data plus its colour. */
export interface DrawLayer {
  readonly d: string;
  readonly fill: string;
}

/**
 * A backend-independent description of the finished symbol.
 *
 * Both renderers consume this: the canvas turns each layer into a `Path2D`,
 * the exporter writes each into a `<path>`. Neither knows anything about QR
 * codes, and the geometry can be unit-tested without a DOM.
 */
export interface QrDrawModel {
  readonly pixelSize: number;
  readonly background: string;
  readonly layers: readonly DrawLayer[];
  /** Modules to be stamped with the user's custom module image. */
  readonly imageModules: readonly Box[];
  /** Where the centre logo goes, if one is set. */
  readonly centerImage: Box | null;
}

export interface BuildModelOptions {
  /** True when a decoded centre image is available to draw. */
  readonly hasCenterImage: boolean;
  /** True when a decoded custom module image is available to draw. */
  readonly hasModuleImage: boolean;
}

export function buildDrawModel(
  matrix: QrMatrix,
  design: QrDesign,
  options: BuildModelOptions,
): QrDrawModel {
  const totalModules = matrix.size + QUIET_ZONE_MODULES * 2;
  const unit = design.size / totalModules;
  const origin = QUIET_ZONE_MODULES * unit;

  const toX = (col: number): number => origin + col * unit;
  const toY = (row: number): number => origin + row * unit;

  const moduleParts: string[] = [];
  const imageModules: Box[] = [];
  const ringParts: string[] = [];
  const pupilParts: string[] = [];

  const clearCenter = options.hasCenterImage;
  const eyesAreModules = design.styleEyesAsModules;

  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (!matrix.isDark(row, col)) continue;

      // The logo sits on top; leave its footprint empty so it reads cleanly.
      if (clearCenter && isCenterAreaModule(row, col, matrix.size, design.centerImageSize)) {
        continue;
      }

      const isEye = isFinderPatternModule(row, col, matrix.size);
      // When the eyes keep their own styling they are drawn once, as whole
      // units, in the pass below — so skip their individual modules here.
      if (isEye && !eyesAreModules) continue;

      const x = toX(col);
      const y = toY(row);

      if (options.hasModuleImage) {
        imageModules.push({ x, y, size: unit });
      } else {
        // Timing and alignment patterns are the scanner's grid reference.
        // Decorating them is the main reason styled codes stop scanning, so
        // they stay solid unless the user has opted into styling everything.
        const style =
          !eyesAreModules && isFunctionPatternModule(row, col, matrix.size)
            ? 'classic'
            : design.moduleStyle;
        moduleParts.push(modulePath(style, x, y, unit, row, col));
      }
    }
  }

  if (!eyesAreModules) {
    const eyeSize = unit * FINDER_PATTERN_SIZE;
    for (const [row, col] of finderPatternOrigins(matrix.size)) {
      const { ring, pupil } = eyePaths(
        design.outerEyeStyle,
        design.innerEyeStyle,
        toX(col),
        toY(row),
        eyeSize,
        design.outerBorderRadius,
        design.innerBorderRadius,
      );
      ringParts.push(ring);
      pupilParts.push(pupil);
    }
  }

  const layers: DrawLayer[] = [];
  const push = (parts: readonly string[], fill: string): void => {
    const d = parts.join('');
    if (d.length > 0) layers.push({ d, fill });
  };

  push(moduleParts, design.moduleColor);
  push(ringParts, design.eyeColor);
  push(pupilParts, design.eyeColor);

  return {
    pixelSize: design.size,
    background: design.backgroundColor,
    layers,
    imageModules,
    centerImage: clearCenter ? centerImageBox(matrix.size, design, unit, origin) : null,
  };
}

/**
 * Places the logo over the cleared modules rather than over an idealised
 * centre, so image and hole stay concentric at every module count.
 */
function centerImageBox(matrixSize: number, design: QrDesign, unit: number, origin: number): Box {
  const { start, end } = centerAreaBounds(matrixSize, design.centerImageSize);
  const span = (end - start + 1) * unit;
  return { x: origin + start * unit, y: origin + start * unit, size: span };
}
